/* eslint-disable no-await-in-loop */
/* eslint-disable no-bitwise */

const ZIP_FILE_HEADER_LENGTH = 30;
const ZIP_FILE_HEADER_SIGNATURE = 0x504b0304;
const ZIP_CENTRAL_HEADER_LENGTH = 46;
const ZIP_CENTRAL_HEADER_SIGNATURE = 0x504b0102;
const ZIP_64_END_RECORD_LENGTH = 56;
const ZIP_64_END_RECORD_SIGNATURE = 0x504b0606;
const ZIP_64_END_LOCATOR_LENGTH = 20;
const ZIP_64_END_LOCATOR_SIGNATURE = 0x504b0607;
const ZIP_END_LENGTH = 22;
const ZIP_END_SIGNATURE = 0x504b0506;

const ZIP_FLAGS_UNICODE_TEXT = 0x0800;

const ZIP_METHOD_STORED = 0x0000;
const ZIP_METHOD_DEFLATED = 0x0008;

function make_crc_table() {
    let c;
    const crcTable = [];
    for (let n = 0; n < 256; n += 1) {
        c = n;
        for (let k = 0; k < 8; k += 1) {
            c = ((c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1));
        }
        crcTable[n] = c;
    }
    return crcTable;
}

const CRC_TABLE = make_crc_table();

async function deflate(buffer) {
    const cs = new window.CompressionStream('deflate');
    const writer = cs.writable.getWriter();
    writer.write(buffer);
    writer.close();
    const output = [];
    const reader = cs.readable.getReader();
    let total_size = 0;
    for (;;) {
        const {
            value,
            done,
        } = await reader.read();
        if (done) {
            break;
        }
        output.push(value);
        total_size += value.byteLength;
    }
    const concatenated = new Uint8Array(total_size);
    let offset = 0;
    for (const array of output) {
        concatenated.set(array, offset);
        offset += array.byteLength;
    }
    return new Uint8Array(concatenated.buffer, 2, total_size - 6);
}

class Entry {
    constructor(path) {
        this.path = path;
        this.size = 0;
        this.method = ZIP_METHOD_STORED;
        this.crc = 0;
    }

    dir() {
        return this.path[this.path.length - 1] === '/';
    }
}

export default class Zip {
    constructor() {
        this.files = {};
        this.workers = {};
    }

    write(path, buffer) {
        const s = path.split('/');
        let spath = '';
        for (let i = 0; i < s.length; i += 1) {
            spath += s[i];
            const dir = (i !== s.length - 1) || (buffer == null);
            if (dir) {
                spath += '/';
            }
            const existing = this.files[spath];
            if (existing == null) {
                this.files[spath] = new Entry(spath);
            }
        }
        if (buffer != null) {
            const entry = this.files[path];
            entry.buffer = buffer;
            entry.method = ZIP_METHOD_STORED;
            entry.size = buffer.byteLength;
            const bytes = new Uint8Array(buffer);
            const length = buffer.byteLength;
            let crc = -1;
            for (let i = 0; i < length; i += 1) {
                crc = (crc >>> 8) ^ CRC_TABLE[(crc ^ bytes[i]) & 0xff];
            }
            entry.crc = (crc ^ (-1)) >>> 0;
            if (window.CompressionStream != null) {
                const self = this;
                let worker;
                const resolve = () => {
                    if (worker != null) {
                        delete self.workers[path];
                    }
                };
                worker = deflate(buffer)
                    .then((data) => {
                        const cmpr = ((data.byteLength * 100) / buffer.byteLength);
                        if (cmpr < 97) {
                            entry.method = ZIP_METHOD_DEFLATED;
                            entry.buffer = data;
                        }
                        resolve();
                    })
                    .catch(() => {
                        entry.method = ZIP_METHOD_STORED;
                        resolve();
                    });
                this.workers[path] = worker;
            }
        }
    }

    dispose() {
        const files = this.files;
        const format = () => new Promise((resolve /* , reject */) => {
            const encoder = new TextEncoder();
            let offset = 0;
            let count = 0;
            let central_size = 0;
            let zip64 = false;
            const centrals = [];
            const data = [];
            const date = new Date();
            const dos_time = date.getSeconds() >> 1 || date.getMinutes() << 5 || date.getHours() << 11;
            const dos_date = date.getDate() || (date.getMonth() + 1) << 5 || (date.getFullYear() - 1980) << 9;
            // eslint-disable-next-line guard-for-in
            for (const name in files) {
                const file = files[name];
                const buffer = file.buffer;
                const encoded = encoder.encode(name);
                const compressed_size = buffer != null ? buffer.byteLength : 0;
                const uncompressed_size = file.size;
                const local_zip64 = compressed_size >= 0xffffffff || uncompressed_size >= 0xffffffff || offset >= 0xffffffff;
                const unicode = encoded.length !== name.length;
                let extra_data_length = local_zip64 ? 20 : 0;
                const header = new DataView(new ArrayBuffer(ZIP_FILE_HEADER_LENGTH + encoded.length + extra_data_length));
                header.setUint32(0, ZIP_FILE_HEADER_SIGNATURE, false); // local file header signature
                header.setUint16(4, 0x002d, true); // version needed to extract
                header.setUint16(6, unicode ? ZIP_FLAGS_UNICODE_TEXT : 0, true); // general purpose bit flag
                header.setUint16(8, file.method, true); // compression method
                header.setUint16(10, dos_time, true); // last mod file time
                header.setUint16(12, dos_date, true); // last mod file date
                header.setUint32(14, file.crc, true); // crc-32
                header.setUint32(18, local_zip64 ? -1 : compressed_size, true); // compressed size
                header.setUint32(22, local_zip64 ? -1 : uncompressed_size, true); // uncompressed size
                header.setUint16(26, encoded.length, true); // file name length
                header.setUint16(28, extra_data_length, true); // extra field length
                for (let i = 0; i < encoded.length; i += 1) {
                    header.setUint8(30 + i, encoded[i]);
                }
                if (local_zip64) {
                    header.setUint16(30 + encoded.length, 0x0001, true);
                    header.setUint16(30 + encoded.length + 2, 16, true);
                    header.setBigUint64(30 + encoded.length + 4, uncompressed_size, true);
                    header.setBigUint64(30 + encoded.length + 12, compressed_size, true);
                }
                data.push(new Uint8Array(header.buffer));
                if (buffer != null) {
                    data.push(new Uint8Array(buffer));
                }
                extra_data_length = 0;
                if (local_zip64) {
                    extra_data_length = 20;
                    if (offset >= 0xffffffff) {
                        extra_data_length += 8;
                    }
                }
                const central = new DataView(new ArrayBuffer(ZIP_CENTRAL_HEADER_LENGTH + encoded.length + extra_data_length));
                central.setUint32(0, ZIP_CENTRAL_HEADER_SIGNATURE, false); // central file header signature
                central.setUint16(4, 0x032d, true); // version made by
                central.setUint16(6, 0x002d, true); // version needed to extract
                central.setUint16(8, unicode ? ZIP_FLAGS_UNICODE_TEXT : 0, true); // general purpose bit flag
                central.setUint16(10, file.method, true); // compression method
                central.setUint16(12, dos_time, true); // last mod file time
                central.setUint16(14, dos_date, true); // last mod file date
                central.setUint32(16, file.crc, true); // crc-32
                central.setUint32(20, local_zip64 ? -1 : compressed_size, true); // compressed size
                central.setUint32(24, local_zip64 ? -1 : uncompressed_size, true); // uncompressed size
                central.setUint16(28, encoded.length, true); // file name length
                central.setUint16(30, extra_data_length, true); // extra field length
                central.setUint16(32, 0, true); // file comment length
                central.setUint16(34, 0, true); // disk number start
                central.setUint16(36, 0, true); // internal file attributes
                central.setUint32(38, file.dir() ? 16 : 0, true); // external file attributes
                central.setUint32(42, offset >= 0xffffffff ? -1 : offset, true); // relative offset of local header
                for (let i = 0; i < encoded.length; i += 1) {
                    central.setUint8(46 + i, encoded[i]);
                }
                if (local_zip64) {
                    central.setUint16(46 + encoded.length, 0x0001, true);
                    central.setUint16(46 + encoded.length + 2, 16, true);
                    central.setBigUint64(46 + encoded.length + 4, uncompressed_size, true);
                    central.setBigUint64(46 + encoded.length + 12, compressed_size, true);
                    if (offset >= 0xffffffff) {
                        central.setBigUint64(46 + encoded.length + 20, offset, true);
                    }
                }
                centrals.push(new Uint8Array(central.buffer));
                count += 1;
                central_size += central.buffer.byteLength;
                offset += header.byteLength;
                if (buffer != null) {
                    offset += buffer.byteLength;
                }
                zip64 |= local_zip64;
            }
            zip64 |= count >= 0xffff || offset >= 0xffffffff;
            if (zip64) {
                const end_zip64 = new DataView(new ArrayBuffer(ZIP_64_END_RECORD_LENGTH + ZIP_64_END_LOCATOR_LENGTH));
                end_zip64.setUint32(0, ZIP_64_END_RECORD_SIGNATURE); // zip64 end of central dir signature
                end_zip64.setBigUint64(4, ZIP_64_END_RECORD_LENGTH - 12, true);
                end_zip64.setUint16(12, 0x032d, true); // version made by
                end_zip64.setUint16(14, 0x002d, true); // version needed to extract
                end_zip64.setUint32(16, 0, true); // number of this disk
                end_zip64.setUint32(20, 0, true); // number of the disk with the start of the central directory
                end_zip64.setBigUint64(24, count, true); // total number of entries in the central directory on this disk
                end_zip64.setBigUint64(32, count, true); // total number of entries in the central directory
                end_zip64.setBigUint64(40, central_size, true); // size of the central directory
                end_zip64.setBigUint64(48, offset, true); // offset of start of central directory with respect to the starting disk number
                end_zip64.setUint32(56, ZIP_64_END_LOCATOR_SIGNATURE); // zip64 end of central dir locator signature
                end_zip64.setUint32(60, 0, true); // number of the disk with the start of the zip64 end of central directory
                end_zip64.setBigUint64(64, offset + central_size, true); // relative offset of the zip64 end of central directory record
                end_zip64.setUint32(72, 1, true); // total number of disks
                centrals.push(new Uint8Array(end_zip64.buffer));
            }
            const end = new DataView(new ArrayBuffer(ZIP_END_LENGTH));
            end.setUint32(0, ZIP_END_SIGNATURE); // end of central dir signature
            end.setUint16(4, 0, true); // number of this disk
            end.setUint16(6, 0, true); // number of the disk with the start of the central directory
            end.setUint16(8, count >= 0xffff ? 0xffff : count, true); // start of the central directory
            end.setUint16(10, count >= 0xffff ? 0xffff : count, true); // total number of entries in the central directory on this disk
            end.setUint32(12, central_size >= 0xffffffff ? 0xffffffff : central_size, true); // size of the central directory
            end.setUint32(16, offset >= 0xffffffff ? 0xffffffff : offset, true); // offset of start of central directory with respect to the starting disk number
            end.setUint16(20, 0, true); // .ZIP file comment length
            centrals.push(new Uint8Array(end.buffer));
            for (let i = 0; i < centrals.length; i += 1) {
                data.push(centrals[i]);
            }
            const blob = new Blob([...data], {
                type: 'application/zip',
            });
            resolve(blob);
        });
        const keys = Object.keys(this.workers);
        const workers = [];
        for (let i = 0; i < keys.length; i += 1) {
            workers.push(this.workers[keys[i]]);
        }
        if (workers.length > 0) {
            return new Promise((resolve, reject) => {
                Promise.all(workers)
                    .then(() => {
                        format()
                            .then(resolve)
                            .catch(reject);
                    })
                    .catch(reject);
            });
        }
        return format();
    }
}
